import React, { useMemo, useCallback, useState } from "react";
import { setSelectedRoles, removeSelectedRolesFromRequest, saveRequest, calcRequestReport, fetchRequestDetailed } from "../../../../reducers/administration/requestSlice";
import { useIntl } from "react-intl";
import CommonTable from "../../../common/table";
import { createRowCellContentInner } from "../../../common/table";
import { Column } from "../../../../types/common/table/table-types";
import { Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import { RequestRole } from "../../../../types/brm/request-types";
import { useNavigate, useParams } from "react-router-dom";
import AddRoleToRequestDialog from "./add-role-to-request-dialog";
import "./styles.less";

const RequestDetailedRolesTable: React.FC = () => {
    const { requestId: requestId } = useParams();
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const roles = useSelector((state: RootState) => state.administration.requests.rolesForView.roles);
    const selectedRoles = useSelector((state: RootState) => state.administration.requests.rolesForView.selectedRoles);
    const currentStage = useSelector((state: RootState) => state.administration.requests.currentRequest?.currentStage);
    const readMode = useSelector((state: RootState) => state.administration.requests.currentRequest?.readMode);
    const currentRequestEditable = useSelector((state: RootState) => state.administration.requests.currentRequest?.currentRequestEditable);
    const groupId = useSelector((state: RootState) => state.administration.requests.currentRequest?.groupId);
    const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
    const disabled = readMode || !currentRequestEditable;

    const createRowCellContent = (
        row: RequestRole,
        column: Column,
        selected: boolean,
        onSelect: (id: string) => void,
        getCellValue: (row: RequestRole, column: Column) => string
    ): JSX.Element => {
        switch (column.id) {
            case "roleType": {
                return <Typography>{`${row.isComplex ? intl.formatMessage({ id: "common.group-role-text" }) : intl.formatMessage({ id: "common.single-role-text" })}`}</Typography>;
            }

            default:
                return createRowCellContentInner(row, column, selected, onSelect, getCellValue);
        }
    };

    const getTableRowId = (row: RequestRole) => row.role;

    const handleRemoveSelectedRoles = useCallback(() => {
        dispatch(removeSelectedRolesFromRequest());
    }, [dispatch]);

    const handleCreateRole = useCallback(() => {
        navigate(`create-new-role`, { relative: "path" });
    }, [navigate]);

    const toolbarActions = useMemo(() => {
        if (currentStage !== "ROLE_DEFINITION") {
            return [];
        }
        return [
            {
                id: "create",
                title: intl.formatMessage({
                    id: "request-roles-table.toolbar.btn-create-roles",
                }),
                position: "begin",
                disabled: disabled,
                callback: handleCreateRole,
            },
            {
                id: "add",
                title: intl.formatMessage({
                    id: "request-roles-table.toolbar.btn-add-role",
                }),
                position: "begin",
                disabled: disabled,
                callback: () => {
                    setAddRoleDialogOpen(true);
                },
            },
            // {
            //     id: "import",
            //     title: intl.formatMessage({
            //         id: "request-roles-table.toolbar.btn-import-roles",
            //     }),
            //     position: "begin",
            //     callback: () => {},
            // },
            {
                id: "conflicts",
                title: intl.formatMessage({
                    id: "request-roles-table.toolbar.btn-conflicts",
                }),
                position: "begin",
                disabled: disabled,
                callback: async () => {
                    if (requestId) {
                        try {
                            await dispatch(saveRequest()).unwrap();
                            await dispatch(fetchRequestDetailed({ requestId: requestId })).unwrap();
                        } catch {
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                        }
                        dispatch(calcRequestReport(requestId));
                    }
                },
            },
            {
                id: "delete",
                title: intl.formatMessage({
                    id: "request-roles-table.toolbar.btn-delete-role",
                }),
                position: "end",
                disabled: disabled,
                callback: handleRemoveSelectedRoles,
            },
            // {
            //     id: "derivation",
            //     title: intl.formatMessage({
            //         id: "request-roles-table.toolbar.btn-derivation",
            //     }),
            //     position: "end",
            //     callback: () => {},
            // },
        ];
    }, [currentStage, disabled, dispatch, intl, handleCreateRole, handleRemoveSelectedRoles, requestId]);

    const getCellValue = (row: RequestRole, column: Column): string => {
        switch (column.id) {
            case "owner":
                if (!row.owner) {
                    return "";
                }

                return row.owner.fullName || row.owner.username;
            case "deputyOwner":
                if (!row.deputyOwner) {
                    return "";
                }

                return row.deputyOwner.fullName || row.deputyOwner.username;
            default:
                return `${row[column.id] ?? ""}`;
        }
    };

    const handleSelectRow = useCallback(
        (row: RequestRole, value: boolean) => {
            dispatch(setSelectedRoles(value ? [...selectedRoles, `${row.role}`] : selectedRoles.filter((id) => id !== `${row.role}`)));
        },
        [selectedRoles, dispatch]
    );

    const handleSelectAll = useCallback(
        (value: boolean) => {
            dispatch(setSelectedRoles(value ? roles.map((role) => String(role.role)) : []));
        },
        [roles, dispatch]
    );

    const columns: Column[] = useMemo(
        () => [
            {
                id: "select",
                type: "select",
            },
            {
                id: "role",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-name" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "groupId",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-landscape" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "description",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-description" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "roleType",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-type" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "owner",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-owner" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "deputyOwner",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.role-deputy-owner" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
            {
                id: "updatedAt",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.updated-at" }),
                type: "datetime",
                sortable: true,
                filterable: true,
            },
            {
                id: "updatedByText",
                title: intl.formatMessage({ id: "request-detailed-roles-table-column.updated-by" }),
                type: "string",
                sortable: true,
                filterable: true,
            },
        ],
        [intl]
    );

    return (
        <>
            <CommonTable
                className="request-roles-table"
                rows={roles as object[]}
                columns={columns as object[]}
                toolbarActions={toolbarActions}
                showPagination={false}
                showFiltersActions={false}
                getCellValue={getCellValue}
                createRowCellContent={createRowCellContent}
                onRowClick={() => {}}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                selectedIds={selectedRoles}
                setFilterData={() => {}}
                useKeyboardNavigation={false}
                toolbar={[]}
                getRowId={getTableRowId}
            />
            <AddRoleToRequestDialog open={addRoleDialogOpen} onClose={() => setAddRoleDialogOpen(false)} groupId={groupId ?? ""} />
        </>
    );
};

export default RequestDetailedRolesTable;
